import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';
import { GLOSSARY } from '@/lib/glossary';
import { cn } from '@/lib/cn';

interface TermProps {
  /** Key in the glossary. */
  k: keyof typeof GLOSSARY;
  /** Optional override of the displayed text (e.g. plural). */
  children?: ReactNode;
  className?: string;
  /** Drop the underline (use when the term is already styled as a header). */
  plain?: boolean;
}

/**
 * Inline term with a dotted underline that reveals a glossary tooltip on
 * hover/focus. Use anywhere a domain word would otherwise be cryptic.
 */
export function Term({ k, children, className, plain }: TermProps) {
  const entry = GLOSSARY[k];
  if (!entry) return <>{children}</>;
  return (
    <Tooltip
      content={
        <div>
          <div className="font-semibold mb-0.5">{entry.term}</div>
          <div className="text-[11px] opacity-90">{entry.short}</div>
        </div>
      }
      maxWidth={300}
    >
      <span
        className={cn(
          !plain && 'underline decoration-dotted decoration-fg-subtle underline-offset-2 cursor-help',
          className,
        )}
        tabIndex={0}
      >
        {children ?? entry.term}
      </span>
    </Tooltip>
  );
}
